import Box from "@material-ui/core/esm/Box";
import Checkbox from "@material-ui/core/esm/Checkbox";
import FormControlLabel from "@material-ui/core/esm/FormControlLabel";
import TextField from "@material-ui/core/esm/TextField";
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { vehicleInformation } from 'api/vehicles/getVehicleInformation';
import isVehicleAlreadyRegistered from 'api/vehicles/isVehicleAlreadyRegistered';
import isVehicleAwaitingShipping from 'api/vehicles/isVehicleAwaitingShipping';
import { FormElement } from 'components/Form';
import InfoBox from 'components/InfoBox/InfoBox';
import { Title } from 'components/Text';
import { licenseIPStandardsRegex, numberRegex } from 'constants/regex';
import useGetValidationStrings from 'hooks/language/useGetValidationString';
import { vehicleFormValid as vehicleFormValidAtom } from 'state/vehicles';
import jsonEqual from 'utils/general/jsonEquals';
import { mapVehicleForm } from 'utils/mapping/mapVehicles';
import checkLoadable from 'utils/recoil/checkLoadable';
import Classification from './Classification/Classification';
import EuClass from './EuClass/EuClass';
import FuelType from './FuelType/FuelType';
import Size from './Size/Size';
import useStyles from './VehicleFormStyles';
export default function InputsInternationalVehicle({
  vehicleFormId
}) {
  const {
    t
  } = useTranslation();
  const {
    register,
    control,
    errors,
    trigger,
    watch,
    formState: {
      isValid
    }
  } = useFormContext();
  const registrationNumber = watch('registrationNumber');
  const nationality = watch('nationality');
  const currentSimpleVehicle = {
    registrationNumber,
    nationality
  };
  const vehicleHasObeLoadable = useRecoilValueLoadable(isVehicleAlreadyRegistered(currentSimpleVehicle));
  const vehicleHasObe = checkLoadable(vehicleHasObeLoadable).contents || false;
  const vehicleHasShippingOrderLoadable = useRecoilValueLoadable(isVehicleAwaitingShipping(currentSimpleVehicle));
  const vehicleHasShippingOrder = checkLoadable(vehicleHasShippingOrderLoadable).contents || false;
  const setVehicleFormValid = useSetRecoilState(vehicleFormValidAtom(vehicleFormId));
  const [vehicleInformationState, setVehicleInformation] = useRecoilState(vehicleInformation(vehicleFormId));
  const vehicleFormStatus = watch();
  useEffect(() => {
    const vehicleFormValid = !vehicleHasObe && !vehicleHasShippingOrder && isValid;
    setVehicleFormValid(vehicleFormValid);
    if (vehicleFormValid) {
      const newVehicleInformationState = mapVehicleForm(vehicleFormStatus, nationality);
      if (!jsonEqual(newVehicleInformationState, vehicleInformationState)) setVehicleInformation(mapVehicleForm(vehicleFormStatus, nationality));
    } else {
      setVehicleInformation(null);
    }
  }, [isValid, vehicleHasObe, vehicleHasShippingOrder, setVehicleFormValid, setVehicleInformation, vehicleFormStatus, nationality, vehicleInformationState]);
  const {
    formInput,
    formInputBox,
    vehicleInputTitle,
    alreadyAttached
    // vehicleDocumentationInfo
  } = useStyles();
  const {
    atLeastOneChar
  } = useGetValidationStrings();
  return /*#__PURE__*/React.createElement(Box, {
    my: 1
  }, /*#__PURE__*/React.createElement("form", null, /*#__PURE__*/React.createElement(Title, {
    className: vehicleInputTitle
  }, t('vehicles.vehicle_details')), /*#__PURE__*/React.createElement(Box, {
    my: 2,
    className: formInputBox
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('vehicles.registration_number'),
    childId: `registrationNumber_${vehicleFormId}`
  }, /*#__PURE__*/React.createElement(TextField, {
    className: formInput,
    name: "registrationNumber",
    inputRef: register({
      required: true,
      minLength: 2,
      pattern: licenseIPStandardsRegex
    }),
    margin: "normal",
    variant: "outlined",
    fullWidth: true,
    error: !!errors.registrationNumber,
    helperText: errors.registrationNumber ? t('validation.registration_number') : null,
    inputProps: {
      id: `registrationNumber_${vehicleFormId}`,
      maxLength: 10
    }
  }))), vehicleHasObe && /*#__PURE__*/React.createElement(InfoBox, {
    mb: 4,
    mt: 2,
    className: alreadyAttached,
    warning: true
  }, t('error.obe_exists_for_vehicle')), vehicleHasShippingOrder && /*#__PURE__*/React.createElement(InfoBox, {
    className: alreadyAttached,
    warning: true
  }, t('error.shipping_order_exists_for_vehicle')), /*#__PURE__*/React.createElement(Classification, {
    control: control
  }), /*#__PURE__*/React.createElement(FuelType, {
    control: control
  }), /*#__PURE__*/React.createElement(EuClass, {
    control: control
  }), /*#__PURE__*/React.createElement(Size, null), /*#__PURE__*/React.createElement(FormElement, {
    title: t('vehicles.brand'),
    childId: `brand_${vehicleFormId}`
  }, /*#__PURE__*/React.createElement(TextField, {
    className: formInput,
    name: "brand",
    inputRef: register({
      required: true,
      minLength: 1
    }),
    variant: "outlined",
    fullWidth: true,
    error: !!errors.brand,
    helperText: errors.brand ? atLeastOneChar : '',
    inputProps: {
      id: `brand_${vehicleFormId}`
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('vehicles.model'),
    childId: `model_${vehicleFormId}`
  }, /*#__PURE__*/React.createElement(TextField, {
    className: formInput,
    name: "model",
    inputRef: register({
      required: true,
      minLength: 1
    }),
    variant: "outlined",
    fullWidth: true,
    error: !!errors.model,
    helperText: errors.model ? atLeastOneChar : '',
    inputProps: {
      id: `model_${vehicleFormId}`
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('vehicles.production_year'),
    childId: `modelYear_${vehicleFormId}`
  }, /*#__PURE__*/React.createElement(TextField, {
    className: formInput,
    name: "modelYear",
    type: "number",
    inputRef: register({
      required: true,
      maxLength: 4,
      minLength: 4,
      pattern: numberRegex
    }),
    defaultValue: new Date().getFullYear(),
    variant: "outlined",
    fullWidth: true,
    error: !!errors.modelYear,
    helperText: errors.modelYear ? t('validation.year') : null,
    inputProps: {
      id: `modelYear_${vehicleFormId}`
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: ""
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      name: "isMobileHome",
      inputRef: register,
      defaultChecked: control.defaultValuesRef.current.isMobileHome,
      onChange: () => trigger('size')
    }),
    label: /*#__PURE__*/React.createElement("strong", null, t('vehicles.is_mobile_home'))
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: `
              ${t('vehicles.color')}
              (${t('vehicles.optional')})
            `,
    childId: `color_${vehicleFormId}`
  }, /*#__PURE__*/React.createElement(TextField, {
    className: formInput,
    name: "color",
    inputRef: register({
      required: false
    }),
    variant: "outlined",
    fullWidth: true,
    error: !!errors.color,
    inputProps: {
      id: `color_${vehicleFormId}`
    }
  }))));
}